import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

export const createCampaignAPi = createAsyncThunk(
  "campaigns/createCampaign",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(apiEndpoints.createCampaign, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data.data;
    } catch (err) {
      console.error("API error:", err.response?.data || err.message);
      return rejectWithValue(
        err.response?.data || { message: "Error creating campaign" },
      );
    }
  },
);

// Thunk to fetch user campaigns
export const fetchUserCampaigns = createAsyncThunk(
  "campaigns/fetchUserCampaigns",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token"); // get token from localStorage
      const response = await axios.get(apiEndpoints.fetchUserCampaigns, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data; // return API response
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching campaigns");
    }
  },
);

export const sendBulkCampaignApi = createAsyncThunk(
  "campaigns/sendBulkCampaign",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.post(
        apiEndpoints.bulkSendCampaign,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      return response.data; // { success: true, message: "Sent" }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Something went wrong";
      return rejectWithValue(errorMessage); // will be available in action.payload
    }
  },
);

export const fetchCampaignById = createAsyncThunk(
  "campaigns/fetchCampaignById",
  async (campaignId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.get(
        `${apiEndpoints.getCampaignById}/${campaignId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data.data; // return single campaign
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching campaign");
    }
  },
);

export const fetchCampaignStatistics = createAsyncThunk(
  "campaigns/fetchCampaignStatistics",
  async (campaignId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const url = apiEndpoints.getCampaignStatistics.replace(
        "{id}",
        campaignId,
      );

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data; // assuming API returns stats under .data
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching campaign statistics",
      );
    }
  },
);

export const sendTrialEmail = createAsyncThunk(
  "campaigns/sendTrialEmail",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.post(apiEndpoints.sendTrialEmail, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data; // { success: true, message: "Sent" }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Something went wrong";
      return rejectWithValue(errorMessage); // will be available in action.payload
    }
  },
);

export const resumeCampaign = createAsyncThunk(
  "campaigns/resumeCampaign",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.post(`${apiEndpoints.resumeCampaign}/${id}`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data; // { success: true, message: "Sent" }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Something went wrong";
      return rejectWithValue(errorMessage); // will be available in action.payload
    }
  },
);

export const campaignStatus = createAsyncThunk(
  "campaigns/campaignStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.get(`${apiEndpoints.campaignStatus}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data; // { success: true, message: "Sent" }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Something went wrong";
      return rejectWithValue(errorMessage); // will be available in action.payload
    }
  },
);

const campaignSlice = createSlice({
  name: "campaigns",
  initialState: {
    campaigns: [],
    campaignStats: {},
    trailemailResposne: {},
    loading: false,
    trailLoading: false,
    error: null,
    currentCampaign: null,
    resumeCampaignData: null,
    campaignStatusData: null,
    createStatus: "idle", // added
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createCampaignAPi.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(createCampaignAPi.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        state.campaigns.push(action.payload); // payload is now a campaign object
      })
      .addCase(createCampaignAPi.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.payload;
      })
      .addCase(fetchUserCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload; // payload is campaign list
      })
      .addCase(fetchUserCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendBulkCampaignApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendBulkCampaignApi.fulfilled, (state, action) => {
        state.loading = false;
        // API might return status/message, not a campaign object
      })
      .addCase(sendBulkCampaignApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.loading = false;
        // Store/update this single campaign
        const currentCampaign = action.payload;

        const existingIndex = state.campaigns.findIndex(
          (c) => c.id === currentCampaign.id,
        );

        if (existingIndex >= 0) {
          state.campaigns[existingIndex] = currentCampaign;
        } else {
          state.campaigns.push(currentCampaign);
        }

        state.currentCampaign = currentCampaign; // ✅ also set it directly
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCampaignStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignStatistics.fulfilled, (state, action) => {
        state.loading = false;
        const stats = action.payload;

        if (stats?.campaign_id) {
          state.campaignStats = action.payload || {};
        }
      })
      .addCase(fetchCampaignStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendTrialEmail.pending, (state) => {
        state.trailLoading = true;
        state.error = null;
      })
      .addCase(sendTrialEmail.fulfilled, (state, action) => {
        state.trailLoading = false;
        state.trailemailResposne = action.payload;
        state.error = null;
      })

      .addCase(sendTrialEmail.rejected, (state, action) => {
        state.trailLoading = false;
        state.error = action.payload;
      })
      .addCase(resumeCampaign.pending, (state) => {
        state.trailLoading = true;
        state.error = null;
      })
      .addCase(resumeCampaign.fulfilled, (state, action) => {
        state.trailLoading = false;
        state.resumeCampaignData = action.payload;
        state.error = null;
      })

      .addCase(resumeCampaign.rejected, (state, action) => {
        state.trailLoading = false;
        state.error = action.payload;
      })
      .addCase(campaignStatus.pending, (state) => {
        state.trailLoading = true;
        state.error = null;
      })
      .addCase(campaignStatus.fulfilled, (state, action) => {
        state.trailLoading = false;
        state.campaignStatusData = action.payload;
        state.error = null;
      })

      .addCase(campaignStatus.rejected, (state, action) => {
        state.trailLoading = false;
        state.error = action.payload;
      });
  },
});

export default campaignSlice.reducer;
